import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, MessageSquare, Lightbulb, AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { createTicket, getTickets } from '../services/api';
import Loader from '../components/Loader';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const TICKET_TYPES = [
  { value: 'support', label: 'Destek', icon: MessageSquare, color: 'bg-blue-100 text-blue-600' },
  { value: 'suggestion', label: `Öneri`, icon: Lightbulb, color: 'bg-green-100 text-green-600' },
  { value: 'complaint', label: `Şikayet`, icon: AlertTriangle, color: 'bg-red-100 text-red-600' },
];

const TicketForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [myTickets, setMyTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'support',
    subject: '',
    message: '',
  });

  useEffect(() => {
    loadMyTickets();
  }, []);

  const loadMyTickets = async () => {
    try {
      const response = await getTickets();
      setMyTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error(`Lütfen tüm alanları doldurun`);
      return;
    }

    setLoading(true);
    try {
      await createTicket(formData);
      toast.success(`Destek talebiniz gönderildi`);
      setFormData({ type: 'support', subject: '', message: '' });
      setShowForm(false);
      loadMyTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Bir hata oluştu`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'pending': return 'Beklemede';
      case 'closed': return `Kapalı`;
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle size={14} className="text-green-500" />;
      case 'pending': return <Clock size={14} className="text-orange-500" />;
      case 'closed': return <XCircle size={14} className="text-gray-500" />;
      default: return null;
    }
  };

  const getTypeInfo = (type) => TICKET_TYPES.find(t => t.value === type) || TICKET_TYPES[0];

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24">
      {/* Header */}
      <header className="bg-white px-4 pt-12 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/panel')}            className="p-2 -ml-2 rounded-full hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-gray-900" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans']">
            Destek Talepleri
          </h1>
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* New Ticket Button */}
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="w-full bg-[#6b6de3] hover:bg-[#5658c4] rounded-full"
            data-testid="new-ticket-btn"
          >
            <Send size={18} className="mr-2" />
            Yeni Destek Talebi
          </Button>
        )}

        {/* Ticket Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="ios-card space-y-4 animate-fade-in">
            <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">
              Yeni Talep
            </h2>

            <div className="space-y-2">
              <Label>{`Talep Türü`}</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                <SelectTrigger className="h-12 bg-[#F2F2F7] border-0 rounded-xl" data-testid="ticket-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TICKET_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Konu</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder={`Konu başlığını yazın...`}
                className="h-12 bg-[#F2F2F7] border-0 rounded-xl"
                required
                data-testid="ticket-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Mesaj</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={`Detaylı açıklama yazın...`}
                className="bg-[#F2F2F7] border-0 rounded-xl min-h-[120px]"
                required
                data-testid="ticket-message-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowForm(false)}
                className="flex-1 rounded-full"
              >
                {`İptal`}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#6b6de3] hover:bg-[#5658c4] rounded-full"
                data-testid="ticket-submit-btn"
              >
                {loading ? `Gönderiliyor...` : `Gönder`}
              </Button>
            </div>
          </form>
        )}

        {/* My Tickets List */}
        <div>
          <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider mb-3">
            Taleplerim
          </h2>
          {loadingTickets ? (
            <div className="py-8 flex justify-center"><Loader size="sm" /></div>
          ) : myTickets.length > 0 ? (
            <div className="space-y-3">
              {myTickets.map((ticket) => {
                const typeInfo = getTypeInfo(ticket.type);
                return (
                  <div key={ticket.id} className={`ios-card ${!ticket.read_by_user && ticket.admin_response ? 'ring-2 ring-[#6b6de3]/30' : ''}`} data-testid={`my-ticket-${ticket.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(ticket.status)}
                          <span className="text-xs text-[#8E8E93]">{getStatusLabel(ticket.status)}</span>
                        </div>
                        {!ticket.read_by_user && ticket.admin_response && (
                          <span className="min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-[#FF3B30] rounded-full px-1">1</span>
                        )}
                      </div>
                      <span className="text-xs text-[#8E8E93]">
                        {new Date(ticket.created_at).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">{ticket.subject}</p>
                    <p className="text-xs text-[#8E8E93] mt-1 line-clamp-2">{ticket.message}</p>
                    
                    {ticket.admin_response && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-[#8E8E93] mb-1">{`Yönetici Yanıtı:`}</p>
                        <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded-xl">{ticket.admin_response}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="ios-card text-center py-8">
              <MessageSquare size={48} className="text-[#C7C7CC] mx-auto mb-3" />
              <p className="text-[#8E8E93]">{`Henüz destek talebiniz yok`}</p>
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default TicketForm;
